import { useEffect, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

/**
 * Automatically sets agent status to "available" on login
 * if the user is a member of an active ring group
 * AND has not manually chosen "offline".
 *
 * Runs once per login session (guarded by ref).
 */
export function useAutoAgentStatus() {
  const { user } = useAuth();
  const ranRef = useRef(false);

  useEffect(() => {
    if (!user?.id || !user?.company_id || ranRef.current) return;
    ranRef.current = true;

    (async () => {
      try {
        // 1. Check if user is in any active ring group
        const { data: memberships } = await (supabase as any)
          .from('ring_group_members')
          .select('id')
          .eq('user_id', user.id)
          .eq('is_active', true)
          .limit(1);

        if (!memberships || memberships.length === 0) return;

        // 2. Check saved manual status preference
        const { data: prefs } = await (supabase as any)
          .from('user_preferences')
          .select('preferences')
          .eq('user_id', user.id)
          .eq('company_id', user.company_id)
          .maybeSingle();

        const savedStatus = (prefs?.preferences as any)?.agent_manual_status;
        if (savedStatus === 'offline') return; // respect manual offline

        // 3. Auto-set to available
        await supabase.from('agent_statuses').upsert(
          {
            user_id: user.id,
            company_id: user.company_id,
            status: 'available',
            updated_at: new Date().toISOString(),
          } as any,
          { onConflict: 'user_id,company_id' }
        );
      } catch (err) {
        console.error('[useAutoAgentStatus] error:', err);
      }
    })();
  }, [user?.id, user?.company_id]);
}
